/*
 * Every account at once (docs/api.md, "Across every account"). On the
 * platform org, a platform admin's Sites, Domains, Mail and Servers lists
 * show Hosterr's own and every account's (agencies, their clients and direct
 * clients), with an Account column, an Account filter (agencies with their
 * clients under them), and each item opening on its own org's pages. Inside
 * an account's org, the info strip says it's being viewed as a Hosterr admin.
 */
import { useEffect, useMemo, useRef, type ReactNode } from "react";
import { Link } from "react-router";
import { useMe } from "../api/queries";
import type { MeOrg } from "../api/types";
import { cx } from "../lib/cx";
import { IconBriefcase, IconInfo } from "../ui/icons";
import type { Facet } from "./FilterBar";
import { OrgContext, useOrg, useOrgPath } from "./org";

/** The account an item is in: its org, and the agency above it for an agency's client. */
export type AccountRef = { id: string; name: string; slug: string; kind: MeOrg["kind"]; agency: { id: string; name: string; slug: string } | null };

/** The `accounts=all` the list APIs take, when this page lists every account. */
export const ACROSS = "all" as const;

/** Whether the signed-in person is a platform admin (an owner or admin of the platform org). */
export function usePlatformAdmin(): boolean {
  const me = useMe();
  return !!(me.data?.user as { platformAdmin?: boolean } | undefined)?.platformAdmin;
}

/** Whether this org's lists cover every account: the platform org, for a platform admin. */
export function useAcross(): boolean {
  const org = useOrg();
  return usePlatformAdmin() && org.kind === "platform";
}

/** An item's account, when the list said (across accounts only). */
export const accountOf = (x: unknown): AccountRef | null => {
  const a = x && typeof x === "object" ? (x as { account?: AccountRef | null }).account : null;
  return a && typeof a === "object" && a.id ? a : null;
};

/** "Bakery · Whitehardt" for an agency's client, else the org's name. */
export const accountLabel = (a: AccountRef) => (a.agency ? `${a.name} · ${a.agency.name}` : a.name);

/** Hosterr's own: in the platform org itself (or no account said). */
export const isOwn = (a: AccountRef | null) => !a || a.kind === "platform";

/** Where an item opens: under its own org's slug when it's another account's (`/o/<its org>/…`). */
export function useAccountPath() {
  const path = useOrgPath();
  const org = useOrg();
  return (a: AccountRef | null | undefined, rest: string) => (a && a.id !== org.id ? `/o/${a.slug}/${rest.replace(/^\//, "")}` : path(rest));
}

/**
 * The org for an account's item, as the panel knows it from /api/me (its role, kind and parent), so its actions,
 * links and dialogs go to that org. The current org when it's Hosterr's own.
 */
export function useAccountOrg() {
  const org = useOrg();
  const me = useMe();
  return useMemo(() => {
    const orgs = new Map((me.data?.orgs ?? []).map((o) => [o.id, o]));
    return (a: AccountRef | null | undefined): MeOrg => (!a || a.id === org.id ? org : (orgs.get(a.id) ?? { ...org, id: a.id, name: a.name, slug: a.slug, kind: a.kind, parentId: a.agency?.id ?? null, billingAlert: null }));
  }, [org, me.data?.orgs]);
}

/** Its children act in the account's org: `useOrg()` inside is the account's. Nothing changes for Hosterr's own. */
export function InAccount({ account, children }: { account: AccountRef | null | undefined; children: ReactNode }) {
  const orgFor = useAccountOrg();
  const value = orgFor(account);
  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

/**
 * The Account filter (`account=` in the address): Hosterr's own first, then the agencies and direct clients by name,
 * each agency's clients under it. Picking an agency takes its clients' items too ("Whitehardt and its clients"); the
 * agency alone is the first choice under it ("Whitehardt only", `<id>.only`). Only accounts with something in the
 * list (and agencies above them).
 */
export function accountFacet<T>(items: T[], of: (item: T) => AccountRef | null, selected: string[] = []): Facet<T> | null {
  const top = new Map<string, { a: { id: string; name: string }; kids: Map<string, string>; self: boolean }>();
  let own = false;
  for (const it of items) {
    const a = of(it);
    if (isOwn(a)) {
      own = true;
      continue;
    }
    if (!a) continue;
    if (a.agency) {
      const t = top.get(a.agency.id) ?? { a: { id: a.agency.id, name: a.agency.name }, kids: new Map(), self: false };
      t.kids.set(a.id, a.name);
      top.set(a.agency.id, t);
    } else {
      const t = top.get(a.id) ?? { a: { id: a.id, name: a.name }, kids: new Map(), self: false };
      t.self = true;
      top.set(a.id, t);
    }
  }
  if (!top.size && !selected.length) return null;
  const byName = (x: [string, string], y: [string, string]) => x[1].localeCompare(y[1], "en", { sensitivity: "base" });
  const options: Facet<T>["options"] = own ? [{ value: "own", label: "Hosterr's own", test: (it) => isOwn(of(it)) }] : [];
  for (const { a, kids, self } of [...top.values()].sort((x, y) => x.a.name.localeCompare(y.a.name, "en", { sensitivity: "base" }))) {
    options.push({ value: a.id, label: kids.size ? `${a.name} and its clients` : a.name, test: (it) => of(it)?.id === a.id || of(it)?.agency?.id === a.id });
    // The agency alone, when it has something of its own here beside its clients'.
    if (kids.size && (self || selected.includes(`${a.id}.only`))) options.push({ value: `${a.id}.only`, label: `${a.name} only`, indent: true, test: (it) => of(it)?.id === a.id });
    for (const [id, name] of [...kids].sort(byName)) options.push({ value: id, label: name, indent: true, test: (it) => of(it)?.id === id });
  }
  return { id: "account", label: "Account", options };
}

/** The account's name, quiet, with a briefcase; it opens the account's Overview. Raised above a card's own link. */
export function AccountChip({ account, className }: { account: AccountRef; className?: string }) {
  const label = accountLabel(account);
  return (
    <Link
      to={`/o/${account.slug}`}
      title={`Account: ${label}`}
      className={cx(
        "relative z-[1] inline-flex h-5.5 max-w-[16rem] min-w-0 shrink-0 items-center gap-1 rounded text-[12px] font-medium text-fg underline-offset-2 transition-colors hover:underline focus-visible:outline-2 focus-visible:outline-brand-500",
        className,
      )}
    >
      <IconBriefcase size={13} className="shrink-0 text-dim" />
      <span className="sr-only">Account: </span>
      <span className="truncate">{label}</span>
    </Link>
  );
}

/** The account as a quiet line with its briefcase, for inside a card that is itself a link (so not a link of its own). */
export function AccountLine({ account, className }: { account: AccountRef; className?: string }) {
  const label = accountLabel(account);
  return (
    <span className={cx("flex min-w-0 items-center gap-1 text-[12.5px] text-body", className)} title={`Account: ${label}`}>
      <IconBriefcase size={13} className="shrink-0 text-dim" />
      <span className="sr-only">Account: </span>
      <span className="truncate">{label}</span>
    </span>
  );
}

/** A table's Account cell: the account (a link to it), or "Hosterr" for the platform's own, quiet. */
export function AccountCell({ account }: { account: AccountRef | null }) {
  const org = useOrg();
  if (isOwn(account)) return <span className="text-dim">{org.name}</span>;
  return (
    <Link to={`/o/${account!.slug}`} className="quiet-link block max-w-[12rem] truncate" title={accountLabel(account!)}>
      {account!.name}
      {account!.agency ? <span className="text-dim"> · {account!.agency.name}</span> : null}
    </Link>
  );
}

/* ── Inside another account ─────────────────────────────────────── */

const viaOf = (o: MeOrg) => (o as MeOrg & { via?: "member" | "agency" }).via;

/**
 * Whether the person reaches `org` only through the platform org (no membership of their own in it or in an org
 * between it and the platform): a platform admin looking at an account. The platform org that leads there, or null.
 */
export function reachedThroughPlatform(org: MeOrg, orgs: MeOrg[]): MeOrg | null {
  if (org.kind === "platform") return null;
  const byId = new Map(orgs.map((o) => [o.id, o]));
  for (let x: MeOrg | undefined = org, n = 0; x && n < 10; x = x.parentId ? byId.get(x.parentId) : undefined, n++) {
    if (viaOf(x) === "member") return x.kind === "platform" ? x : null;
  }
  return null;
}

/**
 * The org switcher's groups for a platform admin: their own organizations, then the accounts they reach through the
 * platform (agencies and direct clients by name, each agency's clients under it). Everyone else: one group, as before.
 */
export function switcherGroups(orgs: MeOrg[], platformAdmin: boolean): { own: MeOrg[]; accounts: { org: MeOrg; indent: boolean }[] } {
  if (!platformAdmin) return { own: orgs, accounts: [] };
  const through = orgs.filter((o) => reachedThroughPlatform(o, orgs));
  const ids = new Set(through.map((o) => o.id));
  const name = (x: MeOrg, y: MeOrg) => x.name.localeCompare(y.name, "en", { sensitivity: "base" });
  const kids = (id: string) => through.filter((o) => o.parentId === id).sort(name);
  const top = through.filter((o) => !o.parentId || !ids.has(o.parentId)).sort(name);
  return { own: orgs.filter((o) => !ids.has(o.id)), accounts: top.flatMap((o) => [{ org: o, indent: false }, ...kids(o.id).map((k) => ({ org: k, indent: true }))]) };
}

/**
 * The app-wide strip under the top bar while a platform admin is inside an account they reach only through the
 * platform: what they're looking at, and the way back. What they do here is recorded as them, as anywhere.
 */
export function AccountStrip() {
  const org = useOrg();
  const me = useMe();
  const platformAdmin = usePlatformAdmin();
  const orgs = me.data?.orgs ?? [];
  const platform = platformAdmin ? reachedThroughPlatform(org, orgs) : null;
  if (!platform) return null;
  const parent = org.parentId ? orgs.find((o) => o.id === org.parentId) : undefined;
  const label = parent && parent.kind === "agency" ? `${org.name} · ${parent.name}` : org.name;
  return (
    // The whole app's state, not a page's: a slim strip in the notices' gray (it's information), like Reconnecting.
    <div role="status" className="flex items-center justify-center gap-2 border-b border-line bg-panel px-5 py-2 text-[13px] text-body sm:px-8">
      <IconInfo size={14} className="shrink-0 text-dim" aria-hidden="true" />
      <span className="min-w-0 truncate">
        Viewing <span className="font-semibold text-fg">{label}</span> as a Hosterr admin
        <span aria-hidden="true" className="text-dim">
          {" "}
          ·{" "}
        </span>
        <Link to={`/o/${platform.slug}`} className="font-medium text-fg underline underline-offset-2">
          Back to {platform.name}
        </Link>
      </span>
    </div>
  );
}

/**
 * The org switcher's search, once there are many: focused when the menu opens, typing stays in it (the menu's own
 * type-to-find is for its items), and the down arrow moves into the list.
 */
export function SwitcherSearch({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const t = setTimeout(() => ref.current?.focus(), 0);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="px-1 pt-0.5 pb-1.5">
      <input
        ref={ref}
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            (e.currentTarget.closest("[role=menu]")?.querySelector("[role=menuitem]") as HTMLElement | null)?.focus();
          } else if (e.key !== "Escape" && e.key !== "Tab") e.stopPropagation();
        }}
        placeholder="Find an organization"
        aria-label="Find an organization"
        className="field h-8 min-h-8 w-full px-2.5 text-[13.5px]"
      />
    </div>
  );
}

/** For sorting by account: Hosterr's own first, then by label. */
export const accountSortKey = (a: AccountRef | null) => (isOwn(a) ? "" : accountLabel(a!).toLowerCase());
